import { useMemo } from 'react';
import styled from 'styled-components';
import type { Role } from '@frameio/graphql-client/generated';
import { Button, Select, theme } from '@frameio/vapor';
import { IconChevronDown } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/MemberAccessRoleSelect';
import { higherZIndexToDisplayRoleDropdownOverDialog } from './values';

TEXT.DEFINE({
  /** Aria label for the role select */
  ariaLabel: (roleName: string) =>
    `Role to apply to invited members: ${roleName}`,
});

const StyledButton = styled(Button)`
  color: ${theme.colors.text.body};
`;

export type MemberAccessRoleSelectProps = {
  availableRoles: Array<Role>;
  onChange: (roleId: Role['id']) => void;
  roleId: Role['id'];
};

export function MemberAccessRoleSelect({
  availableRoles,
  onChange,
  roleId,
}: MemberAccessRoleSelectProps) {
  const currentRoleName = useMemo(
    () => availableRoles.find((role) => role.id === roleId)?.name,
    [availableRoles, roleId]
  );

  const ariaLabel = TEXT.ariaLabel(currentRoleName as string);

  return (
    <Select
      content={availableRoles?.map((role) => ({
        value: role.id,
        description: role.description,
        label: role.name,
      }))}
      name={ariaLabel as string}
      onChange={onChange}
      placement="bottom-end"
      shouldUsePortal={true}
      value={roleId}
      zIndex={higherZIndexToDisplayRoleDropdownOverDialog}
    >
      <StyledButton
        aria-label={ariaLabel as string}
        iconAfter={
          <IconChevronDown color={theme.colors.icon.primary} size={16} />
        }
        size="small"
        variant="link"
      >
        {currentRoleName}
      </StyledButton>
    </Select>
  );
}
